import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
//import { getAuth } from "firebase/auth";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  getUsersForManagement,
  getUserProfile,
  getOpenCorrectionRequests,
  getOrganizationDetails,
  getTestsForUser,
  checkInviteEligibility,
  sendInviteEmail,
  planCleanup,
  executeCleanup,
  sendTestEmail,
  deleteUserAndData,
  getStalledActivations,
} from "./firestoreService";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";

function Dashboard() {
  const { userRole, currentUser } = useAuth();
  const [betaAlertOpen, setBetaAlertOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [requests, setRequests] = useState([]);
  const [organizationName, setOrganizationName] = useState("");
  const [pendingInviteCount, setPendingInviteCount] = useState(0);
  const [staleInviteCount, setStaleInviteCount] = useState(0);
  const [stalledActivations, setStalledActivations] = useState({
    stalledCount: 0,
    expiredCount: 0,
    stalledThresholdDays: 3,
    expiredThresholdDays: 7,
  });

  const [inviteEmail, setInviteEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const [userTestCount, setUserTestCount] = useState(0);
  const [proUserTestCount, setProUserTestCount] = useState(0);

  const [isProcessing, setIsProcessing] = useState(false);
  const [adminActionError, setAdminActionError] = useState("");
  const [adminActionSuccess, setAdminActionSuccess] = useState("");

  const [eligibilityMsg, setEligibilityMsg] = useState("");
  const [eligibilityOK, setEligibilityOK] = useState(false);

  const [planOpen, setPlanOpen] = useState(false);
  const [plan, setPlan] = useState(null); // holds plan payload
  const [confirmText, setConfirmText] = useState("");
  const [execBusy, setExecBusy] = useState(false);
  const canExecute = confirmText.trim().toUpperCase() === "DELETE";

  const [delOpen, setDelOpen] = useState(false);
  const [delEmail, setDelEmail] = useState("");
  const [delPlan, setDelPlan] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");
  const delCanExecute = delConfirm.trim().toUpperCase() === "DELETE";
  const [delBusy, setDelBusy] = useState(false); // ← this is the only busy flag
  const [delError, setDelError] = useState("");
  const [delSuccess, setDelSuccess] = useState("");

  const { t } = useTranslation();

  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async () => {
    if (userRole && currentUser) {
      setLoading(true);
      setError("");
      try {
        const profile = await getUserProfile(currentUser.uid);
        if (!profile) throw new Error("Could not determine user profile.");

        const isSuper = userRole === "superuser";
        const orgId = isSuper ? null : profile.organizationId;

        const [openRequests, managementData] = await Promise.all([
          getOpenCorrectionRequests(orgId),
          getUsersForManagement(),
        ]);

        setRequests(openRequests);
        setPendingInviteCount(managementData.pendingInviteCount);
        setStaleInviteCount(managementData.staleInviteCount || 0);

        if (isSuper) {
          try {
            const stalled = await getStalledActivations();
            setStalledActivations({
              stalledCount: stalled.stalledCount || 0,
              expiredCount: stalled.expiredCount || 0,
              stalledThresholdDays: stalled.stalledThresholdDays || 3,
              expiredThresholdDays: stalled.expiredThresholdDays || 7,
            });
          } catch (e) {
            console.warn("getStalledActivations failed:", e);
          }
        }

        if (!isSuper && profile.organizationId) {
          const orgDetails = await getOrganizationDetails(
            profile.organizationId,
          );
          setOrganizationName(orgDetails?.name || "");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    }
  }, [userRole, currentUser]);

  // --- ✅ FIX: Restored useEffect for Pro User data fetching ---
  useEffect(() => {
    if (userRole === "pro_user" && currentUser) {
      const fetchProData = async () => {
        setLoading(true);
        try {
          const tests = await getTestsForUser(currentUser.uid);
          setProUserTestCount(tests.length);
        } catch (err) {
          console.error("Failed to fetch pro user data", err);
          setError("Could not load your test data.");
        } finally {
          setLoading(false);
        }
      };
      fetchProData();
    }
  }, [userRole, currentUser]);

  useEffect(() => {
    if (userRole === "user" && currentUser) {
      const fetchUserData = async () => {
        setLoading(true);
        try {
          const tests = await getTestsForUser(currentUser.uid);
          setUserTestCount(tests.length);
        } catch (err) {
          console.error("Failed to fetch user data", err);
          setError("Could not load your test data.");
        } finally {
          setLoading(false); // This fixes the infinite spinner
        }
      };
      fetchUserData();
    }
  }, [userRole, currentUser]);

  useEffect(() => {
    if (["admin", "superuser"].includes(userRole)) {
      fetchDashboardData();
    }
  }, [userRole, currentUser, fetchDashboardData]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setEligibilityMsg("");
      setEligibilityOK(false);
      const email = inviteEmail.trim().toLowerCase();
      if (!email) return;
      try {
        const { ok, reason } = await checkInviteEligibility(email);
        if (cancelled) return;
        setEligibilityOK(!!ok);
        setEligibilityMsg(ok ? "" : reason || "This email cannot be invited.");
      } catch (e) {
        if (cancelled) return;
        setEligibilityOK(false);
        setEligibilityMsg(e.message || "Could not validate this email.");
      }
    };
    const t = setTimeout(run, 350); // debounce typing
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [inviteEmail]);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setIsSending(true);
    setInviteError("");
    try {
      // Final guard in UI before calling server
      const { ok, reason } = await checkInviteEligibility(
        inviteEmail.trim().toLowerCase(),
      );
      if (!ok) {
        setInviteError(reason || "This email cannot be invited.");
        setIsSending(false);
        return;
      }
      await sendInviteEmail(inviteEmail);
      alert(`Invite successfully sent to ${inviteEmail}`);
      setInviteEmail("");
      setEligibilityOK(false);
      setEligibilityMsg("");
      fetchDashboardData();
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleCleanup = async () => {
    setIsProcessing(true);
    setAdminActionError("");
    setAdminActionSuccess("");
    try {
      const result = await planCleanup({
        includeStorage: true,
        maxSamples: 10,
      });
      setPlan(result);
      setPlanOpen(true);
    } catch (err) {
      setAdminActionError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderCard = (
    title,
    count,
    description,
    buttonText,
    onButtonClick,
    disabled = false,
    sx = {},
  ) => (
    <Grid item xs={12} sm={6} md={4}>
      <Card
        sx={{
          textAlign: "center",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          ...(sx || {}),
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="h3">
            {title}
          </Typography>
          <Typography variant="h2" component="p" color="primary" sx={{ my: 1 }}>
            {count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "center", p: 2 }}>
          <Button
            variant="contained"
            onClick={onButtonClick}
            disabled={disabled}
            fullWidth
          >
            {buttonText}
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  const renderUserCard = (
    title,
    count,
    description,
    buttonText,
    onButtonClick,
    disabled = false,
  ) =>
    renderCard(title, count, description, buttonText, onButtonClick, disabled, {
      backgroundColor: "#e3f2fd",
    });

  const renderTestCard = (
    title,
    count,
    description,
    buttonText,
    onButtonClick,
    disabled = false,
  ) =>
    renderCard(title, count, description, buttonText, onButtonClick, disabled, {
      backgroundColor: "#e8f5e9",
    });

  const renderStalledCard = (
    title,
    count,
    description,
    buttonText,
    onButtonClick,
    disabled = false,
  ) =>
    renderCard(title, count, description, buttonText, onButtonClick, disabled, {
      backgroundColor: "#fff3e0",
    });

  const renderAdminView = () => (
    <>
      {userRole === "pro_user" && betaAlertOpen && (
        <Alert
          severity="info"
          onClose={() => setBetaAlertOpen(false)}
          sx={{ mb: 3 }}
        >
          <AlertTitle>{t("dashboard.welcomeBetaTitle")}</AlertTitle>
          {t("dashboard.welcomeBetaBody")}{" "}
          <Link
            component={RouterLink}
            to="/app/beta"
            sx={{ fontWeight: "bold" }}
          >
            {t("nav.betaHub")}
          </Link>
          .
        </Alert>
      )}
      <Typography variant="h4" component="h1" gutterBottom>
        {t("dashboard.title")}
      </Typography>
      {organizationName && (
        <Typography variant="h6" component="h2" color="text.secondary">
          {organizationName}
        </Typography>
      )}
      <Typography paragraph>{t("dashboard.welcome")}</Typography>
      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Org-level "verifications pending / verifications confirmed /
            unactivated admins" cards removed 2026-05-22 (Phase 5) — the
            SU-onboarding step is gone, so there's no pendingVerifications
            queue to surface. */}

        {renderUserCard(
          t("dashboard.userPendingInvites"),
          pendingInviteCount,
          staleInviteCount > 0
            ? t("dashboard.invitationsOutstandingWithStale", {
                count: pendingInviteCount,
                stale: staleInviteCount,
                days: 5,
              })
            : t("dashboard.invitationsOutstanding", {
                count: pendingInviteCount,
              }),
          t("dashboard.manageInvites"),
          () => navigate("/app/user-management"),
          pendingInviteCount === 0,
        )}
        {/* User:Pending Approvals card removed 2026-05-22 (Phase 5) — the
            admin approval gate is gone; invitees are created active. */}

        {renderTestCard(
          t("dashboard.testCorrectionRequests"),
          requests.length,
          t("dashboard.testsAwaitingCorrection", { count: requests.length }),
          "View Requests",
          () =>
            navigate("/app/corrections-queue", {
              state: { requestedTestIds: requests.map((r) => r.testId) },
            }),
          requests.length === 0,
        )}

        {userRole === "superuser" &&
          renderStalledCard(
            t("dashboard.stalledActivations"),
            stalledActivations.stalledCount,
            stalledActivations.stalledCount === 0
              ? t("dashboard.stalledActivationsNone")
              : stalledActivations.expiredCount > 0
              ? t("dashboard.stalledActivationsSummaryWithExpired", {
                  count: stalledActivations.stalledCount,
                  expired: stalledActivations.expiredCount,
                  ttl: stalledActivations.expiredThresholdDays,
                })
              : t("dashboard.stalledActivationsSummary", {
                  count: stalledActivations.stalledCount,
                  days: stalledActivations.stalledThresholdDays,
                }),
            t("dashboard.reviewStalled"),
            () => navigate("/app/stalled-activations"),
            false,
          )}

        {userRole === "admin" && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardContent>
                <Typography variant="h6" component="h3">
                  {t("dashboard.inviteNewUser")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("dashboard.inviteDescription")}
                </Typography>
                <Box
                  component="form"
                  onSubmit={handleSendInvite}
                  sx={{ mt: 2 }}
                >
                  <TextField
                    type="email"
                    label={t("dashboard.newUserEmail")}
                    fullWidth
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    disabled={isSending}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={isSending || !inviteEmail || !eligibilityOK}
                  >
                    {isSending ? (
                      <CircularProgress size={24} />
                    ) : (
                      t("dashboard.sendInvite")
                    )}
                  </Button>

                  {eligibilityMsg && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {eligibilityMsg}
                    </Alert>
                  )}
                  {inviteError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {inviteError}
                    </Alert>
                  )}
                </Box>{" "}
                {/* <- close the Box */}
              </CardContent>
            </Card>
          </Grid>
        )}
        {userRole === "superuser" && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t("dashboard.systemMaintenance")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6">{t("dashboard.suTools")}</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t("dashboard.suToolsDescription")}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/app/provisioning")}
                  >
                    {t("dashboard.provisioning")}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/app/backups")}
                  >
                    {t("dashboard.backups")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  {t("dashboard.adminTools")}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t("dashboard.adminToolsWarning")}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleCleanup}
                    disabled={isProcessing}
                  >
                    {t("dashboard.cleanupTestData")}
                  </Button>

                  <Button
                    variant="contained"
                    color="error"
                    onClick={async () => {
                      const email = window.prompt(
                        "Enter the user's email to delete (superuser only):",
                      );
                      if (!email) return;
                      try {
                        setDelBusy(true);
                        setDelError("");
                        setDelSuccess("");
                        const res = await deleteUserAndData({
                          mode: "plan",
                          email: email.trim().toLowerCase(),
                        });
                        if (!res?.ok) {
                          // protected / not_found / etc — show error and DO NOT open modal
                          setDelError(
                            res?.message ||
                              "Cannot build delete plan for this user.",
                          );
                          return;
                        }
                        setDelEmail(email.trim().toLowerCase());
                        setDelPlan(res);
                        setDelOpen(true);
                      } catch (e) {
                        setDelError(
                          e.message || "Failed to build delete plan.",
                        );
                      } finally {
                        setDelBusy(false);
                      }
                    }}
                  >
                    {t("dashboard.deleteUserAndData")}
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ mt: 2 }}
                    onClick={async () => {
                      const targetEmail = window.prompt(
                        "Enter email address to send test email to:",
                      );
                      if (!targetEmail) return;
                      setIsProcessing(true);
                      setAdminActionError("");
                      setAdminActionSuccess("");
                      try {
                        const res = await sendTestEmail({ targetEmail });
                        setAdminActionSuccess(
                          `✅ Test email sent successfully to ${res.sentTo}`,
                        );
                      } catch (err) {
                        console.error("sendTestEmail failed", err);
                        setAdminActionError(
                          `❌ Failed to send test email: ${
                            err.message || "Unknown error"
                          }`,
                        );
                      } finally {
                        setIsProcessing(false);
                      }
                    }}
                  >
                    {t("dashboard.testEmailCredentials")}
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ mt: 2 }}
                    onClick={async () => {
                      const targetEmail = window.prompt(
                        "Recipient email for bilingual template test:",
                      );
                      if (!targetEmail) return;
                      const templateKey =
                        window.prompt(
                          "Template key (default: _test):",
                          "_test",
                        ) || "_test";
                      const locale =
                        window.prompt(
                          "Locale (en, ru, tg, es, pt-BR, vi — default: tg):",
                          "tg",
                        ) || "tg";
                      setIsProcessing(true);
                      setAdminActionError("");
                      setAdminActionSuccess("");
                      try {
                        const res = await sendTestEmail({
                          targetEmail,
                          templateKey,
                          locale,
                        });
                        setAdminActionSuccess(
                          `✅ Template "${res.templateKey}" sent in "${res.locale}" to ${res.sentTo}`,
                        );
                      } catch (err) {
                        console.error("sendTestEmail (template) failed", err);
                        setAdminActionError(
                          `❌ Template test failed: ${
                            err.message || "Unknown error"
                          }`,
                        );
                      } finally {
                        setIsProcessing(false);
                      }
                    }}
                  >
                    Test bilingual email
                  </Button>
                </Box>
                {isProcessing && <CircularProgress sx={{ mt: 2 }} />}
                {adminActionError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {adminActionError}
                  </Alert>
                )}
                {adminActionSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {adminActionSuccess}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Grid>
    </>
  );

  const renderProUserView = () => (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("dashboard.proUserTitle")}
      </Typography>
      <Typography paragraph>{t("dashboard.welcomeActivity")}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {renderCard(
          t("dashboard.myCompletedTests"),
          proUserTestCount,
          t("dashboard.youHaveSubmittedTests", { count: proUserTestCount }),
          t("dashboard.viewMyTests"),
          () => navigate("/app/find-test"),
        )}
      </Grid>
    </>
  );

  const renderUserView = () => (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("dashboard.userTitle")}
      </Typography>
      <Typography paragraph>{t("dashboard.welcomeActivity")}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {renderCard(
          t("dashboard.myTestHistory"),
          userTestCount,
          t("dashboard.youHaveSubmittedTests", { count: userTestCount }),
          t("dashboard.viewMyTests"),
          () => navigate("/app/find-test"),
        )}
      </Grid>
    </>
  );

  if (loading) return <CircularProgress />;
  if (!userRole) return renderUserView();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Dialog
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("dashboard.cleanupPlanTitle")}</DialogTitle>
        <DialogContent dividers>
          {!plan ? (
            <Typography>{t("common.loading")}</Typography>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                {t("dashboard.keepers")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={t("dashboard.usersFormat", {
                      count: plan.keepers.userUids.length,
                    })}
                    secondary={plan.keepers.userUids.join(", ")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t("dashboard.orgsFormat", {
                      count: plan.keepers.orgIds.length,
                    })}
                    secondary={plan.keepers.orgIds.join(", ")}
                  />
                </ListItem>
              </List>

              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {t("dashboard.usersToDelete")}
              </Typography>
              <Typography variant="body2">
                {t("dashboard.count")}: {plan.usersToDelete.count}
              </Typography>
              <List dense>
                {plan.usersToDelete.samples.map((s) => (
                  <ListItem key={s.uid}>
                    <ListItemText
                      primary={`${s.displayName || s.uid} (${s.uid})`}
                    />
                  </ListItem>
                ))}
              </List>

              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {t("dashboard.orgsToDelete")}
              </Typography>
              <Typography variant="body2">
                {t("dashboard.count")}: {plan.orgsToDelete.count}
              </Typography>
              <List dense>
                {plan.orgsToDelete.samples.map((s) => (
                  <ListItem key={s.orgId}>
                    <ListItemText
                      primary={`${s.name || s.orgId} (${s.orgId})`}
                    />
                  </ListItem>
                ))}
              </List>

              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {t("dashboard.transientCollections")}
              </Typography>
              <List dense>
                {plan.collectionsToDelete.map((c) => (
                  <ListItem key={c.name}>
                    <ListItemText
                      primary={`${c.name} — ${c.count}`}
                      secondary={c.samples.join(", ")}
                    />
                  </ListItem>
                ))}
              </List>

              {plan.storage && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {t("dashboard.storage")}
                  </Typography>
                  <Typography variant="body2">
                    {t("dashboard.storagePrefixObjects", {
                      prefix: plan.storage.prefix,
                      count: plan.storage.objectCount,
                    })}
                  </Typography>
                  <List dense>
                    {plan.storage.samples.map((key) => (
                      <ListItem key={key}>
                        <ListItemText primary={key} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                {t("dashboard.reviewCarefullyKeepers")} <strong>NOT</strong>{" "}
                {t("dashboard.reviewCarefullyAppearHere")}
              </Alert>
            </>
          )}
          <TextField
            label={t("dashboard.typeDeleteToConfirm")}
            fullWidth
            margin="dense"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          {/* Day-2: no execute yet */}
          <Button onClick={() => setPlanOpen(false)} disabled={execBusy}>
            {t("common.close")}
          </Button>
          <Button
            onClick={async () => {
              setExecBusy(true);
              setAdminActionError("");
              setAdminActionSuccess("");
              try {
                const res = await executeCleanup({
                  plan: plan.plan,
                  options: { maxDeletes: 100000, preserveKeeperOrgTrees: true },
                });
                setAdminActionSuccess(
                  t("dashboard.cleanupComplete", {
                    count: res.summary.authUsers.deleted,
                    runId: res.cleanupRunId,
                  }),
                );
                setPlanOpen(false);
                setConfirmText("");
                // Optionally refresh your dashboard stats here
              } catch (err) {
                if (
                  String(err.message || "").includes("RECENT_LOGIN_REQUIRED")
                ) {
                  setAdminActionError(t("dashboard.reauthRequired"));
                } else {
                  setAdminActionError(
                    err.message || t("dashboard.executeFailed"),
                  );
                }
              } finally {
                setExecBusy(false);
              }
            }}
            disabled={!canExecute || execBusy}
            color="error"
            variant="contained"
          >
            {t("dashboard.executeCleanup")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={delOpen}
        onClose={() => setDelOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("dashboard.deleteUserPlanTitle")}</DialogTitle>
        <DialogContent dividers>
          {delPlan && delPlan.ok === false && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {delPlan.message || t("dashboard.operationNotAllowed")}
            </Alert>
          )}

          {!delPlan ? (
            <Typography>{t("common.loading")}</Typography>
          ) : (
            <>
              {delPlan?.plan?.protectedUser ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <strong>{delEmail}</strong>{" "}
                  {t("dashboard.protectedUserBlocked")}
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t("dashboard.aboutToDelete")} <strong>{delEmail}</strong>{" "}
                  {t("dashboard.andAllRelatedData")}
                </Alert>
              )}

              {delPlan.plan?.willDeleteOrgTree && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t("dashboard.willDeleteOrgTree")}
                </Alert>
              )}

              {delPlan.plan?.wouldOrphanOrg && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t("dashboard.willOrphanOrg", {
                    orgId: delPlan.plan.organizationId,
                  })}
                </Alert>
              )}

              {/* Example fields to display from your plan payload */}
              <Typography variant="subtitle1">{t("dashboard.user")}</Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={`name: ${delPlan.plan?.displayName || "—"}`}
                    secondary={`uid: ${delPlan.plan?.uid || "?"}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`role: ${delPlan.plan?.role || "?"}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`organization: ${
                      delPlan.plan?.organizationName || "—"
                    }`}
                    secondary={`organizationId: ${
                      delPlan.plan?.organizationId || "?"
                    }`}
                  />
                </ListItem>
              </List>

              <Typography variant="h6">
                {t("dashboard.firestoreDeletes")}
              </Typography>
              {delPlan.plan?.collections?.length ? (
                <List dense>
                  {delPlan.plan.collections.map((col, i) => (
                    <ListItem key={i}>
                      <ListItemText
                        primary={`${col.path} (${col.count} docs)`}
                        secondary={
                          col.sampleIds?.length
                            ? `Sample IDs: ${col.sampleIds.join(", ")}`
                            : col.note || null
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("dashboard.noneFound")}
                </Typography>
              )}

              <Typography variant="h6" sx={{ mt: 2 }}>
                {t("dashboard.storageDeletes")}
              </Typography>
              {delPlan.plan?.storage?.count ? (
                <>
                  <Typography variant="body2">
                    {delPlan.plan.storage.count} object
                    {delPlan.plan.storage.count !== 1 ? "s" : ""} under{" "}
                    {delPlan.plan.storage.bucketPrefix}
                  </Typography>
                  <List dense>
                    {delPlan.plan.storage.samples?.map((obj, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={obj} />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("dashboard.noneFound")}
                </Typography>
              )}

              {delPlan.orgAction && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {t("dashboard.organizationAction")}
                  </Typography>
                  <Alert
                    severity={
                      delPlan.orgAction.action === "delete" ? "error" : "info"
                    }
                  >
                    {delPlan.orgAction.message}
                  </Alert>
                </>
              )}

              {delError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {delError}
                </Alert>
              )}
              {delSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {delSuccess}
                </Alert>
              )}
            </>
          )}

          <TextField
            label={t("dashboard.typeDeleteToConfirm")}
            fullWidth
            margin="dense"
            value={delConfirm}
            onChange={(e) => setDelConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelOpen(false)} disabled={delBusy}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!delCanExecute || delBusy || !delPlan}
            onClick={async () => {
              try {
                setDelBusy(true);
                setDelError("");
                const res = await deleteUserAndData({
                  mode: "delete",
                  email: delEmail,
                  plan: delPlan.plan,
                  options: { hardDelete: true },
                });
                setDelSuccess(
                  t("dashboard.deletedRunId", {
                    runId: res.cleanupRunId || "(n/a)",
                  }),
                );
                setDelOpen(false);
                setDelEmail("");
                setDelPlan(null);
                setDelConfirm("");
                // optionally refresh dashboard stats here
              } catch (e) {
                setDelError(e.message || t("dashboard.executeFailed"));
              } finally {
                setDelBusy(false);
              }
            }}
          >
            {t("dashboard.executeNoGoingBack")}
          </Button>
        </DialogActions>
      </Dialog>

      {(() => {
        if (userRole === "pro_user") {
          return renderProUserView();
        } else if (["admin", "superuser"].includes(userRole)) {
          return renderAdminView();
        } else if (userRole === "user") {
          return renderUserView();
        } else {
          return <Typography>{t("dashboard.loadingExperience")}</Typography>;
        }
      })()}
    </Container>
  );
}

export default Dashboard;
